import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Loader2, BookOpen, Trash2, Plus,
  ChevronDown, ChevronUp, GripVertical, HelpCircle, X,
  FileText, Clock, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Lesson Editor Component (lazy-loads content on expand) ──
const LessonEditor = ({ lesson, courseId, token, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState(null);
  const [quizForm, setQuizForm] = useState(null);
  const [quizDirty, setQuizDirty] = useState(false);
  const [savingQuiz, setSavingQuiz] = useState(false);

  // Only init form data when expanded
  const handleExpand = () => {
    if (!expanded && !form) {
      setForm({
        title: lesson.title || '',
        content: lesson.content || '',
        content_type: lesson.content_type || 'markdown',
        duration_minutes: lesson.duration_minutes || 15,
        short_summary: lesson.short_summary || '',
        module_title: lesson.module_title || '',
        is_preview: lesson.is_preview || false,
      });
      setQuizForm({
        title: lesson.quiz?.title || 'Quiz',
        passing_score: lesson.quiz?.passing_score || 70,
        questions: lesson.quiz?.questions?.map(q => ({
          question_text: q.question_text,
          options: [...q.options],
          correct_answer_index: q.correct_answer_index,
        })) || [],
      });
    }
    setExpanded(!expanded);
  };

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const saveLesson = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/courses/${courseId}/lessons/${lesson.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Lesson "${form.title}" saved`);
      setDirty(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lesson');
    } finally { setSaving(false); }
  };

  const saveQuiz = async () => {
    setSavingQuiz(true);
    try {
      await axios.put(`${API}/admin/courses/${courseId}/lessons/${lesson.id}/quiz`, quizForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Quiz saved');
      setQuizDirty(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save quiz');
    } finally { setSavingQuiz(false); }
  };

  const addQuestion = () => {
    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, { question_text: '', options: ['', '', '', ''], correct_answer_index: 0 }],
    }));
    setQuizDirty(true);
  };

  const removeQuestion = (qi) => {
    setQuizForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== qi) }));
    setQuizDirty(true);
  };

  const updateQuestion = (qi, field, value) => {
    setQuizForm(prev => {
      const questions = [...prev.questions];
      questions[qi] = { ...questions[qi], [field]: value };
      return { ...prev, questions };
    });
    setQuizDirty(true);
  };

  const updateOption = (qi, oi, value) => {
    setQuizForm(prev => {
      const questions = [...prev.questions];
      const options = [...questions[qi].options];
      options[oi] = value;
      questions[qi] = { ...questions[qi], options };
      return { ...prev, questions };
    });
    setQuizDirty(true);
  };

  const deleteLesson = async () => {
    if (!window.confirm(`Delete lesson "${lesson.title}"? This also removes its quiz.`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}/lessons/${lesson.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lesson deleted');
      onDelete();
    } catch (err) {
      toast.error('Failed to delete lesson');
    }
  };

  const quizCount = lesson.quiz?.questions?.length || 0;

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`lesson-editor-${lesson.id}`}>
      {/* Lesson Header */}
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={handleExpand}
      >
        <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
        <span className="text-sm text-slate-400 w-6">{lesson.order || '-'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800 truncate">{lesson.title}</span>
            {lesson.is_preview && <Badge variant="outline" className="text-xs">Preview</Badge>}
          </div>
          {lesson.module_title && <span className="text-xs text-slate-400">{lesson.module_title}</span>}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Clock className="w-3 h-3" /> {lesson.duration_minutes}m
          {quizCount > 0 && (
            <Badge className="text-xs bg-purple-100 text-purple-700">{quizCount} Q</Badge>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>

      {/* Expanded Content - only rendered when expanded and form initialized */}
      {expanded && form && (
        <div className="border-t border-slate-100 p-4 space-y-6">
          {/* Lesson Fields */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Lesson Details
              </h4>
              <div className="flex gap-2">
                {dirty && (
                  <Button size="sm" onClick={saveLesson} disabled={saving} data-testid={`lesson-save-btn-${lesson.id}`}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save Lesson
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={deleteLesson} data-testid={`lesson-delete-btn-${lesson.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Title</Label>
                <Input value={form.title} onChange={(e) => updateForm('title', e.target.value)} data-testid={`lesson-title-input-${lesson.id}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Module</Label>
                <Input value={form.module_title} onChange={(e) => updateForm('module_title', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Content Type</Label>
                <Select value={form.content_type} onValueChange={(v) => updateForm('content_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="markdown">Markdown</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Duration (min)</Label>
                <Input type="number" min="1" value={form.duration_minutes} onChange={(e) => updateForm('duration_minutes', parseInt(e.target.value) || 1)} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_preview} onCheckedChange={(c) => updateForm('is_preview', c)} />
                <Label className="text-xs">Free Preview</Label>
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Summary</Label>
                <Input value={form.short_summary} onChange={(e) => updateForm('short_summary', e.target.value)} />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Content (Markdown) — use <code className="bg-slate-100 px-1 rounded text-xs">![alt](url)</code> to add images</Label>
                <Textarea value={form.content} onChange={(e) => updateForm('content', e.target.value)} rows={14} className="font-mono text-sm" data-testid={`lesson-content-input-${lesson.id}`} />
              </div>
            </div>
          </div>

          {/* Quiz Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                <HelpCircle className="w-4 h-4" /> Quiz ({quizForm.questions.length} questions)
              </h4>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={addQuestion} data-testid={`quiz-add-question-${lesson.id}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add Question
                </Button>
                {quizDirty && (
                  <Button size="sm" onClick={saveQuiz} disabled={savingQuiz} data-testid={`quiz-save-btn-${lesson.id}`}>
                    {savingQuiz ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save Quiz
                  </Button>
                )}
              </div>
            </div>

            {quizForm.questions.length === 0 ? (
              <p className="text-sm text-slate-400 italic">No quiz questions. Click "Add Question" to create one.</p>
            ) : (
              <div className="space-y-4">
                {quizForm.questions.map((q, qi) => (
                  <div key={qi} className="border border-slate-100 rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`quiz-question-${lesson.id}-${qi}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-slate-400 mt-2">Q{qi + 1}</span>
                      <div className="flex-1">
                        <Input
                          value={q.question_text}
                          onChange={(e) => updateQuestion(qi, 'question_text', e.target.value)}
                          placeholder="Question text..."
                          className="text-sm"
                        />
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => removeQuestion(qi)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`correct-${lesson.id}-${qi}`}
                            checked={q.correct_answer_index === oi}
                            onChange={() => updateQuestion(qi, 'correct_answer_index', oi)}
                            className="accent-green-600"
                          />
                          <Input
                            value={opt}
                            onChange={(e) => updateOption(qi, oi, e.target.value)}
                            placeholder={`Option ${oi + 1}`}
                            className={`text-sm ${q.correct_answer_index === oi ? 'border-green-400 bg-green-50' : ''}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Add Lesson Form Component ──
const AddLessonForm = ({ courseId, token, lessonCount, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    content: '',
    content_type: 'markdown',
    duration_minutes: 15,
    short_summary: '',
    is_preview: false,
  });

  const reset = () => {
    setForm({ title: '', content: '', content_type: 'markdown', duration_minutes: 15, short_summary: '', is_preview: false });
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/courses/${courseId}/lessons`, {
        ...form,
        order: lessonCount + 1,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Module added successfully');
      reset();
      setOpen(false);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add module');
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" className="w-full border-dashed border-2 py-6" onClick={() => setOpen(true)} data-testid="add-module-btn">
        <Plus className="w-4 h-4 mr-2" /> Add New Module
      </Button>
    );
  }

  return (
    <Card className="border-2 border-primary/30 bg-primary/5" data-testid="add-module-form">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2"><Plus className="w-5 h-5" /> New Module</CardTitle>
          <Button variant="ghost" size="icon" onClick={() => { reset(); setOpen(false); }}><X className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label className="text-xs font-medium">Title *</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Module 13: Advanced Topics" data-testid="add-module-title-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Content (Markdown)</Label>
          <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} placeholder="Write your module content in markdown..." data-testid="add-module-content-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs font-medium">Duration (minutes)</Label>
            <Input type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 15 })} data-testid="add-module-duration-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium">Summary</Label>
            <Input value={form.short_summary} onChange={(e) => setForm({ ...form, short_summary: e.target.value })} placeholder="Brief summary..." data-testid="add-module-summary-input" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.is_preview} onCheckedChange={(c) => setForm({ ...form, is_preview: c })} />
          <Label className="text-xs">Free Preview</Label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => { reset(); setOpen(false); }}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-new-module-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Add Module
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Main Course Editor Page ──
const AdminEditCourse = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [courseForm, setCourseForm] = useState({});

  const fetchCourse = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/courses/${courseId}/full`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourse(res.data);
      setCourseForm({
        title: res.data.title || '',
        description: res.data.description || '',
        price: res.data.price || 0,
        thumbnail_url: res.data.thumbnail_url || '',
        category: res.data.category || 'general',
        tier: res.data.tier || 'regular',
        is_published: res.data.is_published ?? true,
      });
    } catch (err) {
      toast.error('Failed to load course');
      navigate('/admin/courses');
    } finally { setLoading(false); }
  }, [courseId, token, navigate]);

  useEffect(() => {
    if (user?.role !== 'admin') { toast.error('Admin access required'); navigate('/'); return; }
    fetchCourse();
  }, [user, navigate, fetchCourse]);

  const saveCourseDetails = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/courses/${courseId}`, courseForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Course details saved');
      fetchCourse();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </div>
    );
  }

  if (!course) return null;

  const lessons = course.lessons || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-edit-course">
      <Navbar />
      <div className="pt-24 pb-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/admin/courses')} className="-ml-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="edit-course-title">
                  Edit: {course.title}
                </h1>
                <p className="text-sm text-slate-500">{lessons.length} lessons | {course.total_duration_minutes || 0} min total</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(`/courses/${courseId}`)}>
              <Eye className="w-4 h-4 mr-2" /> View Course
            </Button>
          </div>

          {/* Course Details Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <BookOpen className="w-5 h-5" /> Course Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Title</Label>
                  <Input value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} data-testid="edit-course-title-input" />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Textarea value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} rows={3} data-testid="edit-course-description-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Price ($)</Label>
                  <Input type="number" min="0" step="0.01" value={courseForm.price} onChange={(e) => setCourseForm({ ...courseForm, price: parseFloat(e.target.value) || 0 })} data-testid="edit-course-price-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Category</Label>
                  <Select value={courseForm.category} onValueChange={(v) => setCourseForm({ ...courseForm, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="ai-tools">AI Tools</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="productivity">Productivity</SelectItem>
                      <SelectItem value="development">Development</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tier</Label>
                  <Select value={courseForm.tier} onValueChange={(v) => setCourseForm({ ...courseForm, tier: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Thumbnail URL</Label>
                  <Input value={courseForm.thumbnail_url} onChange={(e) => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })} placeholder="https://..." data-testid="edit-course-thumbnail-input" />
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <Switch checked={courseForm.is_published} onCheckedChange={(c) => setCourseForm({ ...courseForm, is_published: c })} />
                  <Label className="text-xs">Published</Label>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={saveCourseDetails} disabled={saving} data-testid="save-course-details-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Course Details
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lessons Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-slate-900">Modules ({lessons.length})</h2>
            </div>
            {lessons.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-400">
                  <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No modules yet. Import via JSON or add them manually below.</p>
                </CardContent>
              </Card>
            ) : (
              lessons.map((lesson) => (
                <LessonEditor
                  key={lesson.id}
                  lesson={lesson}
                  courseId={courseId}
                  token={token}
                  onUpdate={fetchCourse}
                  onDelete={fetchCourse}
                />
              ))
            )}
            <AddLessonForm courseId={courseId} token={token} lessonCount={lessons.length} onAdded={fetchCourse} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEditCourse;
